import React from 'react';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import GetAppIcon from '@material-ui/icons/GetApp';
import LaunchIcon from '@material-ui/icons/Launch';
import Skeleton from '@material-ui/lab/Skeleton';
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";

import BaseWeekStoreDashboardView from '../BaseWeekStoreDashboardView';
import { statGreen, statOrange, statRed } from "../../globals";
import Slider from "@material-ui/core/Slider";
import { formatRatioPercent } from "../../utils";
import { formatFloat, unitFormatter } from '../../utils';
import {observer} from "mobx-react";
import {action, computed, observable} from "mobx";
import Header from "../../components/utils/Header";
import FilterListView from "../../components/FilterListView";
import conditionalFormats from "../../store/conditionalFormats";


const styles = theme => ({
  kpiCard: {
    height: '100%',
    padding: theme.spacing(2),
    // borderLeft: `10px solid ${statGreen}`,
    // borderTopLeftRadius: 0,
    // borderBottomLeftRadius: 0,
    overflow: 'hidden',
  },
  green: {
    background: statGreen,
    color: 'white',
  },
  orange: {
    background: statOrange,
    color: 'white',
    // borderColor: statOrange,
  },
  red: {
    background: statRed,
    color: 'white',
    // borderColor: statRed,
  },
  grey: {
    background: "lightgrey",
    color: 'white',
    // borderColor: statGrey,
  },
  kpiContainer: {
    height: `calc(100% + ${theme.spacing(2)}px)`,
  },
  kpiCardTitle: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 20,
    color: 'inherit',
  },
  kpiDownloadButton: {
    width: `calc(100% + ${theme.spacing(4)}px)`,
    marginBottom: -theme.spacing(2),
    marginLeft: -theme.spacing(2),
    borderRadius: 0,
  },
});


@withStyles(styles)
class KpiCard extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    downloadUrl: PropTypes.string,
    downloadLabel: PropTypes.string,
    color: PropTypes.string,
  };
  static defaultProps = {
    downloadUrl: null,
    downloadLabel: 'Download',
    color: null,
  };

  render() {
    const { title, children, downloadUrl, downloadLabel, color, classes } = this.props;
    return (
      <Paper className={`${classes.kpiCard} ${classes[color]}`}>
        <Grid
          container direction="column" justify="space-between" spacing={2} wrap="nowrap" className={classes.kpiContainer}
        >
          <Grid item>
            <Typography variant="h6" className={classes.kpiCardTitle}>{title}</Typography>
          </Grid>
          <Grid item>
            {children}
          </Grid>
          <Grid item>
            <Button
              href={downloadUrl}
              component="a"
              startIcon={<GetAppIcon />}
              color="inherit"
              fullWidth
              className={classes.kpiDownloadButton}
            >
              {downloadLabel}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}


const kpiRowStyles = theme => ({
  title: {
    // fontWeight: theme.typography.fontWeightLight,
  },
  subtitle: {
    fontWeight: theme.typography.fontWeightLight,
    fontSize: '0.75rem',
  },
  value: {
    fontWeight: theme.typography.fontWeightBold,
  },
});


@withStyles(kpiRowStyles)
class KpiRow extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    value: PropTypes.string.isRequired,
  };
  static defaultProps = {
    subtitle: null,
  };

  render() {
    const { title, subtitle, value, classes } = this.props;
    return (
      <Grid container justify="space-between">
        <Grid item>
          <div className={classes.title}>{title}</div>
          {subtitle !== null && <div className={classes.subtitle}>{subtitle}</div>}
        </Grid>
        <Grid item>
          <div className={classes.value}>{value}</div>
        </Grid>
      </Grid>
    );
  }
}


class KpiCardComparisonContent extends React.Component {

  static propTypes = {
    kpiDetails: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        subtitle: PropTypes.string,
        value: PropTypes.string.isRequired,
      })
    )
  };

  render() {
    const { kpiDetails } = this.props;
    return (
      <Grid container direction="column" spacing={2} wrap="nowrap">
      {kpiDetails.map(({title, subtitle, value}, index) =>
        <Grid item key={index}>
          <KpiRow title={title} subtitle={subtitle} value={value} />
        </Grid>
      )}
      </Grid>
    );
  }
}


@observer
class KpiCardThresholdContent extends React.Component {
  static propTypes = {
    thresholdDescription: PropTypes.func.isRequired,
    threshold: PropTypes.number.isRequired,
    onThresholdChange: PropTypes.func.isRequired,
    unit: PropTypes.string.isRequired,
    kpiData: PropTypes.object.isRequired,
    volumeFormatter: PropTypes.func.isRequired,
    ratioFormatter: PropTypes.func.isRequired,
  };

  valueLabelFormat = value => formatRatioPercent(value, 0);

  @computed get formattedThreshold() {
    return this.valueLabelFormat(this.props.threshold);
  }

  render() {
    const { thresholdDescription, threshold, onThresholdChange, kpiData, volumeFormatter, ratioFormatter } = this.props;
    const { skus, skusRatio, volume, volumeRatio } = kpiData;
    return (
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item>
          <strong>{skus} products</strong> (<strong>{ratioFormatter(skusRatio)}</strong> of total goods){' '}
          <strong>{volumeFormatter(volume)}</strong> (<strong>{ratioFormatter(volumeRatio)}</strong> of total volume){' '}
          {thresholdDescription(this.formattedThreshold)}
        </Grid>
        <Grid item>
          <Grid container spacing={2} justify="space-between">
            <Grid item>Alert threshold</Grid>
            <Grid item>{this.formattedThreshold}</Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Slider
            min={0.0}
            step={0.01}
            max={1.0}
            value={threshold}
            onChange={onThresholdChange}
            valueLabelFormat={this.valueLabelFormat}
            valueLabelDisplay="auto"
          />
        </Grid>
      </Grid>
    );
  }
}

@observer
class KpiCardTextContent extends React.Component {
  static propTypes = {
    description: PropTypes.string.isRequired,
    unit: PropTypes.string.isRequired,
    kpiData: PropTypes.object.isRequired,
    volumeFormatter: PropTypes.func.isRequired,
    ratioFormatter: PropTypes.func.isRequired,
  };

  render() {
    const { description, kpiData, volumeFormatter, ratioFormatter } = this.props;
    const { skus, skusRatio, volume, volumeRatio } = kpiData;
    return (
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item>
          <strong>{skus} products</strong> (<strong>{ratioFormatter(skusRatio)}</strong> of total goods){' '}
          <strong>{volumeFormatter(volume)}</strong> (<strong>{ratioFormatter(volumeRatio)}</strong> of total volume){' '}
          {description}
        </Grid>
      </Grid>
    );
  }
}

@observer
class WeeklyCockpitView extends BaseWeekStoreDashboardView {

  static apiSubPath = '/weekly_cockpit/';

  static units = {
    TO: 'Tons',
    KPC: 'Pieces',
    L: 'Liters',
    kEUR: 'Euros',
  };

  static horizons = {
    4: 'W+4',
    16: 'W+16',
  };

  stockFormatter = unitFormatter('t', 0);
  kPiecesFormatter = unitFormatter('k', 0);
  literFormatter = unitFormatter('L', 0);
  euroFormatter = unitFormatter('k€', 0);

  volumeFormatter = value => {
    if (this.unit === "TO") {
      return this.stockFormatter(value);
    }
    if (this.unit === "KPC") {
      return this.kPiecesFormatter(value);
    }
    if (this.unit === "L") {
      return this.literFormatter(value);
    }
    if (this.unit === "kEUR") {
      return this.euroFormatter(value);
    }
    return formatFloat(value, 0);
  };

  ratioFormatter = value => formatRatioPercent(value, 0);

  @observable unit = Object.keys(this.constructor.units)[0];
  @observable horizon = Object.keys(this.constructor.horizons)[0];
  @observable thresholdDemand = 0.15;
  @observable thresholdProduction = 0.15;

  _setData(data) {
    if ('week_store' in data && data.week_store) {
      this.weekStoreId = parseInt(data.week_store);
    }
    if (data.horizon) {
      this.horizon = data.horizon;
    }
  }

  _getQueryParams() {
    return {
        horizon: this.horizon,
        threshold_demand_changes: this.thresholdDemand,
        threshold_production_changes: this.thresholdProduction,
    };
  }

  onUnitChange = this.genericChangeHandler('unit', false);

  @action onThresholdDemandChange = (event, value) => {
    this.thresholdDemand = value;
    this.debouncedUpdateData();
  };
  @action onThresholdProductionChange  = (event, value) => {
    this.thresholdProduction = value;
    this.debouncedUpdateData();
  };
  @action onHorizonChange = event => {
    this.horizon = event.target.value;
    this.updateData();
  };

  getDownloadUrl = (fileName) => {
    return this.weekStore.getApiUrl('/weekly_cockpit/?' + this.getQueryString({ export: 'xlsx', kpi_filename: fileName }))
  };

  getAlertsKpis = (kpiName) => {
    const data = this.data.kpis.alerts;
    return {
      skus: data[kpiName][this.unit].skus,
      skusRatio: data[kpiName][this.unit].skus_ratio,
      volume: data[kpiName][this.unit].volume,
      volumeRatio: data[kpiName][this.unit].volume_ratio, 
   };
  };

  getColorName = (conditionalFormatName, value) => (
    conditionalFormats[conditionalFormatName].getColorName(value)
  );

  renderLastWeekKpis() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={250} />;
    }
    const data = this.data.kpis.comparison;
    const { production, planned_production, production_delta_volume, production_delta_ratio } = data.produced_vs_planned[this.unit];
    const { stock, planned_stock, stock_delta_volume, stock_delta_ratio } = data.stocked_vs_planned[this.unit];
    const { actuals_demand, forecasted_demand, demand_delta_volume, demand_delta_ratio } = data.demand_vs_planned[this.unit];
    const { back_orders, back_orders_ratio, back_orders_nb_skus, back_orders_nb_skus_ratio } = data.weekly_back_orders[this.unit];

    const pastBackOrders = this.data.kpis.past.weekly_back_orders[this.unit];
    const past_back_orders = pastBackOrders.back_orders;
    const past_back_orders_ratio = pastBackOrders.back_orders_ratio;
    const past_back_orders_nb_skus = pastBackOrders.back_orders_nb_skus;
    const past_back_orders_nb_skus_ratio = pastBackOrders.back_orders_nb_skus_ratio;

    return (
      <>
        <Typography variant="h5" gutterBottom>Last week & current KPIs</Typography>
        <Grid container spacing={2}>
          <Grid item xs>
            <KpiCard
              title="Produced vs. planned"
              color={this.getColorName('weekly-produced-vs-planned', production / planned_production)}
              downloadLabel="Production detail"
              downloadUrl={this.getDownloadUrl("produced_vs_planned")}
            >
              <KpiCardComparisonContent
                kpiDetails={[
                  {title: "Produced", value: this.volumeFormatter(production)},
                  {title: "Planned", subtitle: "(Production plan W-1)", value: this.volumeFormatter(planned_production)},
                  {title: "Produced - Planned", value: `${this.volumeFormatter(production_delta_volume)} (${this.ratioFormatter(production_delta_ratio)})`},
                ]} />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard
              title="Stock vs. planned"
              color={this.getColorName('weekly-stock-vs-planned', stock / planned_stock)}
              downloadLabel="Stock detail"
              downloadUrl={this.getDownloadUrl("stocked_vs_planned")}
            >
              <KpiCardComparisonContent
                kpiDetails={[
                  {title: "Stock", value: this.volumeFormatter(stock)},
                  {title: "Planned", subtitle: "(Production plan W-1)", value: this.volumeFormatter(planned_stock)},
                  {title: "Stock - Planned", value: `${this.volumeFormatter(stock_delta_volume)} (${this.ratioFormatter(stock_delta_ratio)})`},
                ]} />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard
              title="Demand (Actuals) vs. planned"
              color={this.getColorName('weekly-demand-vs-planned', actuals_demand / forecasted_demand)}
              downloadLabel="Demand detail"
              downloadUrl={this.getDownloadUrl("demand_vs_planned")}
            >
              <KpiCardComparisonContent
                kpiDetails={[
                  {title: "Actuals", value: this.volumeFormatter(actuals_demand)},
                  {title: "Planned", subtitle: "(Demand forecast W-1)", value: this.volumeFormatter(forecasted_demand)},
                  {title: "Actuals - Planned", value: `${this.volumeFormatter(demand_delta_volume)} (${this.ratioFormatter(demand_delta_ratio)})`},
                ]}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard title="Current back-orders" downloadLabel="Back-orders detail" downloadUrl={this.getDownloadUrl("weekly_back_orders")}>
              <KpiCardComparisonContent
                kpiDetails={[
                  {title: "SKUs", subtitle: "(% vs. portfolio)", value: `${formatFloat(back_orders_nb_skus, 0)} (${this.ratioFormatter(back_orders_nb_skus_ratio)})`},
                  {title: "W-1", value: `${formatFloat(past_back_orders_nb_skus, 0)} (${this.ratioFormatter(past_back_orders_nb_skus_ratio)})`},
                  {},
                  {title: "Volume", subtitle: "(% vs. portfolio)", value: `${this.volumeFormatter(back_orders)} (${this.ratioFormatter(back_orders_ratio)})`},
                  {title: "W-1", value: `${this.volumeFormatter(past_back_orders)} (${this.ratioFormatter(past_back_orders_ratio)})`},
                ]}
              />
            </KpiCard>
          </Grid>
        </Grid>
      </>
    );
  }

  renderIncomingWeeklyKpis() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={250} />;
    }
    const data = this.data.kpis.incoming;
    const asset_utilization = data.asset_utilization.asset_utilization;
    const { total_stock, stock_vs_target } = data.stock[this.unit];
    const { service_level } = data.service_level;

    const pastData = this.data.kpis.past;
    const past_asset_utilization = pastData.asset_utilization.asset_utilization
    const past_total_stock = pastData.stock[this.unit].total_stock;
    const past_stock_vs_target = pastData.stock[this.unit].stock_vs_target;
    const past_service_level = pastData.service_level.service_level;

    return (
      <>
        <Typography variant="h5" gutterBottom>Incoming weekly KPIs</Typography>
        <Grid container spacing={2}>
          <Grid item xs>
            <KpiCard
              title="Asset utilization"
              color={this.getColorName('weekly-incoming-capacity-utilization', (asset_utilization.FG + asset_utilization.SFG) / 2)}
              downloadLabel="Capacity detail"
              downloadUrl={this.getDownloadUrl("asset_utilization")}
            >
              <KpiCardComparisonContent
                kpiDetails={[
                  {title: "FG capacity utilization", value: this.ratioFormatter(asset_utilization.FG)},
                  {title: "W-1", value: this.ratioFormatter(past_asset_utilization.FG)},
                  {},
                  {title: "SFG capacity utilization", value: this.ratioFormatter(asset_utilization.SFG)},
                  {title: "W-1", value: this.ratioFormatter(past_asset_utilization.SFG)},
                ]}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard
              title="Stock position (end of horizon)"
              downloadLabel="Stock detail"
              downloadUrl={this.getDownloadUrl("stock")}
            >
              <KpiCardComparisonContent
                kpiDetails={[
                  {title: "Total FG stock", subtitle: "(% vs FG stock target)", value: `${this.volumeFormatter(total_stock.FG)} (${this.ratioFormatter(stock_vs_target.FG)})`},
                  {title: "W-1", value: `${this.volumeFormatter(past_total_stock.FG)} (${this.ratioFormatter(past_stock_vs_target.FG)})`},
                  {},
                  {title: "Total FG stock", subtitle: "(% vs SFG stock target)", value: `${this.volumeFormatter(total_stock.SFG)} (${this.ratioFormatter(stock_vs_target.SFG)})`},
                  {title: "W-1", value: `${this.volumeFormatter(past_total_stock.SFG)} (${this.ratioFormatter(past_stock_vs_target.SFG)})`},
                ]}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard
              title="Service level"
              color={this.getColorName('weekly-service-level', service_level)}
              downloadLabel="Service level detail"
              downloadUrl={this.getDownloadUrl("service_level")}
            >
              <KpiCardComparisonContent
                kpiDetails={[
                  {title: "Service level", subtitle: "(average value during horizon)", value: this.ratioFormatter(service_level)},
                  {title: "W-1", value: this.ratioFormatter(past_service_level)},
                ]}
              />
            </KpiCard>
          </Grid>
          <Grid item xs container alignItems="center">
            <Grid item xs>
              <Button
                variant="contained"
                component="a"
                fullWidth
                size="large"
                startIcon={<LaunchIcon />}
                color="inherit"
                href="https://danone-my.sharepoint.com/:f:/r/personal/sopsn-o365_danone_com/Documents/SN%20Sales%20and%20Operations%20Planning/10.%20Performance/01.%20KPI%20reporting%20process/CSL/Historical%20pdfs?csf=1&web=1&e=VufGvU"
                target="_blank"
              >
                CSL report
              </Button>
            </Grid>
          </Grid>
          <Grid item xs container alignItems="center">
            <Grid item xs>
              <Button
                variant="contained"
                component="a"
                fullWidth
                size="large"
                startIcon={<LaunchIcon />}
                color="inherit"
                target="_blank"
              >
                RP report
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }

  renderAlerts() {
    if (!this.ready) {
      return <Skeleton variant="rect" height={250} />;
    }
    return (
      <>
        <Typography variant="h5" gutterBottom>Alerts</Typography>
        <Grid container spacing={2}>
          <Grid item xs>
            <KpiCard title="Demand changes" downloadUrl={this.getDownloadUrl("demand_changes")}>
              <KpiCardThresholdContent
                thresholdDescription={threshold => `have more than ± ${threshold} demand forecast change vs. last week`}
                threshold={this.thresholdDemand}
                onThresholdChange={this.onThresholdDemandChange}
                unit={this.unit}
                kpiData={this.getAlertsKpis("demand_changes")}
                volumeFormatter={this.volumeFormatter}
                ratioFormatter={this.ratioFormatter}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard title="Production changes" downloadUrl={this.getDownloadUrl("production_changes")}>
              <KpiCardThresholdContent
                thresholdDescription={threshold => `have more than ± ${threshold} production plan change vs. last week`}
                threshold={this.thresholdProduction}
                onThresholdChange={this.onThresholdProductionChange}
                unit={this.unit}
                kpiData={this.getAlertsKpis("production_changes")}
                volumeFormatter={this.volumeFormatter}
                ratioFormatter={this.ratioFormatter}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard title="Stock below min" downloadUrl={this.getDownloadUrl("stock_below_min")}>
              <KpiCardTextContent
                description={`are at risk of understock in the next ${this.horizon} weeks`}
                unit={this.unit}
                kpiData={this.getAlertsKpis("stock_below_min")}
                volumeFormatter={this.volumeFormatter}
                ratioFormatter={this.ratioFormatter}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard title="Stock above max" downloadUrl={this.getDownloadUrl("stock_above_max")}>
              <KpiCardTextContent
                description={`are at risk of overstock in the next ${this.horizon} weeks`}
                unit={this.unit}
                kpiData={this.getAlertsKpis("stock_above_max")}
                volumeFormatter={this.volumeFormatter}
                ratioFormatter={this.ratioFormatter}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard title="Out of stock" downloadUrl={this.getDownloadUrl("stock_out")}>
              <KpiCardTextContent
                description={`are at risk of out-of-stock in the next ${this.horizon} weeks`}
                unit={this.unit}
                kpiData={this.getAlertsKpis("stock_out")}
                volumeFormatter={this.volumeFormatter}
                ratioFormatter={this.ratioFormatter}
              />
            </KpiCard>
          </Grid>
          <Grid item xs>
            <KpiCard title="Dead stock" downloadUrl={this.getDownloadUrl("dead_stock")}>
              <KpiCardTextContent
                description={'are currently dead stock'}
                unit={this.unit}
                kpiData={this.getAlertsKpis("dead_stock")}
                volumeFormatter={this.volumeFormatter}
                ratioFormatter={this.ratioFormatter}
              />
            </KpiCard>
          </Grid>
        </Grid>
      </>
    );
  }

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container justify="flex-end" alignItems="center" spacing={4}>
        <Grid item>
          {this.weekStoreField}
        </Grid>
        <Grid item>
          <TextField variant="outlined" select label="Unit" value={this.unit} onChange={this.onUnitChange}>
            {Object.entries(this.constructor.units).map(([value, name]) =>
              <MenuItem key={value} value={value}>{name}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item>
          <TextField variant="outlined" select label="Horizon" value={this.horizon} onChange={this.onHorizonChange}>
            {Object.entries(this.constructor.horizons).map(([value, name]) =>
              <MenuItem key={value} value={value}>{name}</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
      </Grid>
    );
  }

  get inputFilesDownload() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <>
        <Grid item>
          <Typography variant="h5" gutterBottom> Weekly input files (raw) </Typography>
        </Grid>
        <Grid item container spacing={2}>
        {[
          {fileName: "initial_stock_file", displayName: "Stock"},
          {fileName: "sap_production_plan_file", displayName: "SAP production plan"},
          {fileName: "apo_weekly_production_plan_file", displayName: "APO production plan"},
          {fileName: "demand_forecasts_file", displayName: "Demand forecasts"},
          {fileName: "demand_actuals_file", displayName: "Demand actuals"},
          {fileName: "location_to_cbu_mapping_file", displayName: "Mapping Location-CBU"},
          {fileName: "stock_target_file", displayName: "Parthenos stock target"},
          {fileName: "scenario_url", displayName: "Athena Scenario"},
        ].map(({fileName, displayName})=>(
          <Grid item xs={12} sm={6} md={4} xl={2} key={fileName}>
            <Button
              href={this.weekStore[fileName]}
              component="a"
              startIcon={<GetAppIcon />}
              variant="contained"
              fullWidth
              target="_blank"
            >
              {displayName}
            </Button>
        </Grid>
        ))}
        </Grid>
      </>
    );
  }

  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid item>
          <Grid container justify="space-between" alignItems="center" spacing={1} wrap="nowrap">
            <Grid item>
              <Header title="Weekly Cockpit" />
            </Grid>
            <Grid item>
              {this.controls}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {this.renderLastWeekKpis()}
        </Grid>
        <Grid item>
          {this.renderIncomingWeeklyKpis()}
        </Grid>
        <Grid item>
          {this.renderAlerts()}
        </Grid>
        <Grid item>
          {this.inputFilesDownload}
        </Grid>
      </Grid>
    );
  }
}


export default WeeklyCockpitView;
